"use client";
import {
  useEffect,
  useState,
  createContext,
  useContext,
  ReactNode,
} from "react";
import {
  pageView,
  track,
  trackTyped,
  isAnalyticsReady,
} from "@/lib/analytics/gtag";
import { usePathname } from "next/navigation";
import { initWebVitals } from "@/lib/analytics/webvitals";
//

interface AnalyticsContextValue {
  track: typeof track;
  trackTyped: typeof trackTyped;
  consent: boolean;
  setConsent: (value: boolean) => void;
  setConsentDecision: (value: "allow" | "deny") => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(
  undefined,
);

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<boolean>(() => false);
  const [decision, setDecision] = useState<null | "allow" | "deny">(null);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  // Read persisted consent decision on mount to avoid SSR/CSR mismatch
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const saved =
          typeof window !== "undefined"
            ? localStorage.getItem("analytics_consent_decision")
            : null;
        if (saved === "allow" || saved === "deny") {
          setDecision(saved);
        } else {
          setDecision(null);
        }
      } catch (err) {
        console.warn("Failed to read analytics consent", err);
      }
    }, 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (decision === "allow") {
      window.__analyticsConsent = true;
      // Update GA4 Consent Mode to grant analytics after user allows
      try {
        if (typeof window.gtag === "function") {
          window.gtag("consent", "update", {
            ad_storage: "denied",
            analytics_storage: "granted",
            functionality_storage: "granted",
            personalization_storage: "denied",
          });
        }
      } catch {}
      const t = setTimeout(() => setConsent(true), 0);
      return () => clearTimeout(t);
    } else if (decision === "deny") {
      window.__analyticsConsent = false;
      // Ensure GA4 remains denied to prevent cookies
      try {
        if (typeof window.gtag === "function") {
          window.gtag("consent", "update", {
            ad_storage: "denied",
            analytics_storage: "denied",
            functionality_storage: "denied",
            personalization_storage: "denied",
          });
        }
      } catch {}
      const t = setTimeout(() => setConsent(false), 0);
      return () => clearTimeout(t);
    }
  }, [decision]);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!consent) return;
    if (!isAnalyticsReady()) return;
    pageView(pathname);
    // Initialize Web Vitals after consent
    initWebVitals();
  }, [pathname, consent]);

  return (
    <AnalyticsContext.Provider
      value={{
        track,
        trackTyped,
        consent,
        setConsent: (v) => {
          // keep legacy API functional
          setDecision(v ? "allow" : "deny");
        },
        setConsentDecision: (v) => {
          try {
            setDecision(v);
            localStorage.setItem("analytics_consent_decision", v);
            window.__analyticsConsent = v === "allow";
          } catch (err) {
            console.warn("Consent decision error", err);
          }
        },
      }}
    >
      {/* Simple consent banner */}
      {mounted && decision === null && (
        <div className="fixed bottom-4 left-4 right-4 z-60">
          <div className="mx-auto max-w-3xl rounded-lg border bg-card p-4 shadow-lg">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                We use analytics to improve the experience. Allow anonymous
                usage tracking?
              </p>
              <div className="flex gap-2">
                <button
                  className="px-3 py-1 text-sm rounded-md border"
                  onClick={() => {
                    try {
                      setDecision("deny");
                      localStorage.setItem(
                        "analytics_consent_decision",
                        "deny",
                      );
                      window.__analyticsConsent = false;
                    } catch (err) {
                      console.warn("Consent toggle error", err);
                    }
                  }}
                >
                  Decline
                </button>
                <button
                  className="px-3 py-1 text-sm rounded-md bg-primary text-primary-foreground"
                  onClick={() => {
                    try {
                      setDecision("allow");
                      localStorage.setItem(
                        "analytics_consent_decision",
                        "allow",
                      );
                      window.__analyticsConsent = true;
                    } catch (err) {
                      console.warn("Consent toggle error", err);
                    }
                  }}
                >
                  Allow
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  const ctx = useContext(AnalyticsContext);
  if (!ctx)
    throw new Error("useAnalytics must be used within AnalyticsProvider");
  return ctx;
}

export function AnalyticsRouteTracker() {
  // Exists so it can be referenced in layout after children
  return null;
}
